import { useEffect, useState } from "react";
import { CheckCircle2, ClipboardList, RotateCcw } from "lucide-react";
import { Button } from "../../ui/Button.js";
import { useTr, type Entry } from "../../i18n/context.js";
import "./AgentConfirmation.css";

export type AgentConfirmationKind = "create" | "edit" | "adapt";

export interface AgentConfirmationContextItem {
  readonly label: string;
  readonly value: string;
  readonly tone?: "neutral" | "warning" | "danger";
}

export interface AgentConfirmationQuestion {
  readonly id?: string;
  readonly question: string;
  readonly suggestedAnswer?: string;
  readonly options?: readonly AgentConfirmationOption[];
}

export interface AgentConfirmationAnswer {
  readonly id?: string;
  readonly question: string;
  readonly answer: string;
}

export interface AgentConfirmationOption {
  readonly id?: string;
  readonly label: string;
  readonly value: string;
  readonly description?: string;
}

export interface AgentConfirmationBrief {
  readonly kind: AgentConfirmationKind;
  readonly title: string;
  readonly summary: string;
  readonly steps: readonly string[];
  readonly contextItems: readonly AgentConfirmationContextItem[];
  readonly questions?: readonly AgentConfirmationQuestion[];
}

const S = {
  badge: { zh: "需要你确认", en: "Confirm first" },
  context: { zh: "上下文", en: "Context" },
  steps: { zh: "执行步骤", en: "Steps" },
  questions: { zh: "需要确认", en: "Confirm" },
  suggested: { zh: "建议默认", en: "Suggested" },
  extra: { zh: "补充说明", en: "Extra notes" },
  confirm: { zh: "确认并开始", en: "Confirm and start" },
  confirmContinue: { zh: "确认并继续", en: "Confirm and continue" },
  revise: { zh: "调整输入", en: "Revise input" },
} satisfies Record<string, Entry>;

export function AgentConfirmationCard({
  brief,
  onConfirm,
  onRevise,
  compact = false,
}: {
  readonly brief: AgentConfirmationBrief;
  readonly onConfirm: (answers?: readonly AgentConfirmationAnswer[]) => void;
  readonly onRevise: () => void;
  readonly compact?: boolean;
}): JSX.Element {
  const tr = useTr();
  const questions = brief.questions ?? [];
  const [answers, setAnswers] = useState<string[]>(() => questions.map((question) => question.suggestedAnswer ?? ""));

  useEffect(() => {
    setAnswers(questions.map((question) => question.suggestedAnswer ?? ""));
  }, [brief]);

  function updateAnswer(index: number, value: string): void {
    setAnswers((current) => current.map((item, i) => (i === index ? value : item)));
  }

  function confirm(): void {
    if (questions.length === 0) {
      onConfirm();
      return;
    }
    onConfirm(
      questions.map((question, index) => {
        const answer = (answers[index] ?? "").trim() || question.suggestedAnswer || "User confirmed without an extra answer.";
        return { id: question.id, question: question.question, answer };
      }),
    );
  }

  return (
    <section
      className="lw-agent-confirm"
      data-kind={brief.kind}
      data-compact={compact || undefined}
      data-has-questions={questions.length > 0 || undefined}
      aria-label={brief.title}
    >
      <header className="lw-agent-confirm__head">
        <span className="lw-agent-confirm__icon" aria-hidden="true">
          <ClipboardList size={16} />
        </span>
        <span className="lw-agent-confirm__head-copy">
          <span className="lw-agent-confirm__badge">{tr(S.badge)}</span>
          <span className="lw-agent-confirm__title">{brief.title}</span>
        </span>
      </header>

      <p className="lw-agent-confirm__summary">{brief.summary}</p>

      {brief.contextItems.length > 0 ? (
        <div className="lw-agent-confirm__context" aria-label={tr(S.context)}>
          {brief.contextItems.map((item) => (
            <span
              key={`${item.label}:${item.value}`}
              className="lw-agent-confirm__context-chip"
              data-tone={item.tone ?? "neutral"}
            >
              <span className="lw-agent-confirm__context-label">{item.label}</span>
              <span className="lw-agent-confirm__context-value">{item.value}</span>
            </span>
          ))}
        </div>
      ) : null}

      {questions.length > 0 ? (
        <div className="lw-agent-confirm__questions" aria-label={tr(S.questions)}>
          <span className="lw-agent-confirm__questions-title">{tr(S.questions)}</span>
          <div className="lw-agent-confirm__question-list">
            {questions.map((question, index) => (
              <div key={question.id ?? `${index}:${question.question}`} className="lw-agent-confirm__question">
                <span className="lw-agent-confirm__question-index" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="lw-agent-confirm__question-body">
                  <span className="lw-agent-confirm__question-text">{question.question}</span>
                  {question.suggestedAnswer ? (
                    <span className="lw-agent-confirm__question-suggested">
                      <span className="lw-agent-confirm__suggested-label">{tr(S.suggested)}</span>
                      <span className="lw-agent-confirm__suggested-value">{question.suggestedAnswer}</span>
                    </span>
                  ) : null}
                  {question.options?.length ? (
                    <div className="lw-agent-confirm__options" role="radiogroup" aria-label={question.question}>
                      {question.options.map((option) => {
                        const selected = (answers[index] ?? "").trim() === option.value.trim();
                        return (
                          <button
                            key={option.id ?? option.value}
                            type="button"
                            role="radio"
                            className="lw-agent-confirm__option"
                            data-selected={selected || undefined}
                            aria-checked={selected}
                            onClick={() => updateAnswer(index, option.value)}
                          >
                            <span className="lw-agent-confirm__option-mark" aria-hidden="true" />
                            <span className="lw-agent-confirm__option-copy">
                              <span className="lw-agent-confirm__option-label">{option.label}</span>
                              {option.description ? (
                                <span className="lw-agent-confirm__option-desc">{option.description}</span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  <label className="lw-agent-confirm__answer-wrap">
                    <span className="lw-agent-confirm__answer-label">{tr(S.extra)}</span>
                    <textarea
                      className="lw-agent-confirm__answer"
                      aria-label={question.question}
                      rows={2}
                      value={answers[index] ?? ""}
                      placeholder={question.suggestedAnswer ?? ""}
                      onChange={(event) => updateAnswer(index, event.target.value)}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="lw-agent-confirm__steps">
        <span className="lw-agent-confirm__steps-title">{tr(S.steps)}</span>
        <ol className="lw-agent-confirm__steps-list">
          {brief.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>

      <div className="lw-agent-confirm__actions">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leadingIcon={<RotateCcw size={14} aria-hidden="true" />}
          onClick={onRevise}
        >
          {tr(S.revise)}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          leadingIcon={<CheckCircle2 size={14} aria-hidden="true" />}
          onClick={confirm}
        >
          {questions.length > 0 ? tr(S.confirmContinue) : tr(S.confirm)}
        </Button>
      </div>
    </section>
  );
}
